import { Injectable } from '@angular/core';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { IPortfolioCompanyContact } from '@common/models/IPortfolioCompanyContact';
import { ApiService } from '@app/core/services/api.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { contactArrayFields, contactSingleValueFields } from '@common/constants/contact.constant';
import { LocalStorageKey } from '@app/core/enums/local-storage-key.enum';

/** Internal Quantum email domains - use Graph API for these */
const INTERNAL_DOMAINS = ['quantumcap.com', 'quantumep.com'];

@Injectable({
  providedIn: 'root'
})
export class ContactsService {

  private externalContactsCache: IPortfolioCompanyContact[] = [];

  constructor(private readonly apiService:ApiService) { }

  getAllPorfolioCompanies(): Observable<IPortfolioCompany[]> {
    return this.apiService.get<IPortfolioCompany[]>('PortfolioCompany')
  } 

  getUserByEmail(userEmail:string){
    return this.apiService.get('User/guest/' + userEmail);
  }

  getGuestUserByEmail(email: string): Observable<any> {
    const cached = localStorage[LocalStorageKey.guestUserCache];
    if (cached) {
      const guestUsers = JSON.parse(cached);
      const user = guestUsers.find((prop: any) => prop?.mail?.toLowerCase() === email?.toLowerCase());
      if (user) {
        return of(user);
      } else {
        return this.getUserByEmail(email);
      }
    } else {
      // Optionally, fetch from API if not cached
      return this.getUserByEmail(email);
    }
  }

  fetchUniqueGuestEmails(): Observable<any> {
    return this.getAllPorfolioCompanies().pipe(
      map((data) => {
        const uniqueEmailList = new Set<string>();
        for (const item of data) {
          this.processContacts(item, uniqueEmailList);
        }
        return Array.from(uniqueEmailList);
      }),
      switchMap((uniqueEmailList: string[]) =>
        this.getGuestUserEmailList(uniqueEmailList)
      )
    );
  }

  /**
   * Extract and process other contact information
   */
  private processContacts(item: any, uniqueEmailList: Set<string>) {
    const otherContactKeys = contactArrayFields;
    const singleValueKeys = contactSingleValueFields;
    const contactData: any = {};

    for (const key of otherContactKeys) {
      const contacts = (item.otherContact?.[key] || []).filter(
        (email) => !!email && email.trim() !== ''
      );
      contactData[key] = [...contacts].sort();
      this.addToUniqueEmails(contacts, uniqueEmailList);
    }

    for (const key of singleValueKeys) {
      const raw = item.otherContact?.[key];
      const contacts = raw ? [raw] : [];
      contactData[key] = contacts;
      this.addToUniqueEmails(contacts, uniqueEmailList);
    }

    return contactData;
  }

  /**
   * Adds emails to the unique set
   */
  private addToUniqueEmails(list: string[], uniqueEmailList: Set<string>) {
    for (const email of list) {
      uniqueEmailList.add(email);
    }
  }

  getGuestUserEmailList(userEmailList:string[]){
    return this.apiService.post('User/guestBulkGet/' , userEmailList );
  }

  /**
   * Get all external contacts from the PortfolioCompanyContact API
   */
  getAllExternalContacts(): Observable<IPortfolioCompanyContact[]> {
    return this.apiService.get<IPortfolioCompanyContact[]>('PortfolioCompanyContact');
  }

  /**
   * Get external contacts for a specific portfolio company
   */
  getExternalContactsByMnemonic(mnemonic: string): Observable<IPortfolioCompanyContact[]> {
    return this.apiService.get<IPortfolioCompanyContact[]>(`PortfolioCompanyContact/${mnemonic}`);
  }

  /**
   * Check if an email belongs to an internal Quantum domain
   */
  isInternalEmail(email: string): boolean {
    if (!email) return false;
    const domain = email.toLowerCase().split('@')[1];
    return INTERNAL_DOMAINS.includes(domain);
  }

  /**
   * Get contact display info by email.
   * Internal emails use Graph API, external emails use the contact data.
   */
  getContactDisplayInfo(email: string, externalContacts: IPortfolioCompanyContact[]): Observable<{ name: string; email: string; isInternal: boolean }> {
    if (this.isInternalEmail(email)) {
      // Internal user - use Graph API
      return this.getUserByEmail(email).pipe(
        map((user: any) => ({
          name: user?.name || email,
          email: email,
          isInternal: true
        }))
      );
    } else {
      // External user - look up in contacts list
      const contact = externalContacts.find(c => c.email?.toLowerCase() === email.toLowerCase());
      return of({
        name: contact?.name || email,
        email: email,
        isInternal: false
      });
    }
  }

  prepareOfficeLocation(address: any) {
    let location = null;

    if (address?.street) {
      location = address?.street;
    }
    if (address?.city) {
      location = location ? `${location}, ${address?.city}` : `${address?.city}`;
    }
    if (address?.state) {
      location = location ? `${location}, ${address?.state}` : `${address?.state}`;
    }
    if (address?.country) {
      location = location ? `${location}, ${address?.country}` : `${address?.country}`;
    }

    return location
  }

  /**
   * Prepare user data from external user data
   * @param contact 
   * @returns 
   */
  getContactDetailsInfo(contact: IPortfolioCompanyContact): any {
    const userData = {
      email: contact.email,
      name: contact.name,
      displayName: contact.name,
      firstName: contact.firstName,
      lastName: contact.lastName,
      jobTitle: contact.jobTitle,
      roles: contact.roles,
      company: contact.company,
      businessPhone: contact.businessPhone,
      mobilePhone: contact.mobilePhone,
      officeLocation: contact?.address ? this.prepareOfficeLocation(contact?.address) : null,
      isExternal: true,
      sharePointId: contact?.sharePointId
    };
    return userData;
  }

  /**
   * Prepare user data from API User/guest user data
   * @param user 
   * @returns 
   */
  getInternalContactDetailsInfo(user: any): any {
    const userData = {
      email: user?.mail || user?.email,
      name: user?.givenName,
      displayName: user?.displayName,
      firstName: user?.givenName,
      lastName: user?.surname,
      jobTitle: user?.jobTitle,
      roles: user?.roles || [],
      company: user?.company || null,
      businessPhone: user?.businessPhone?.length ? user?.businessPhone[0] : null,
      mobilePhone: user?.mobilePhone,
      officeLocation: user?.officeLocation,
      isInternal: true,
      photo: user?.phone || null
    };
    return userData;
  }

  setExternalContactsCache(data: IPortfolioCompanyContact[] = []) {
    this.externalContactsCache = data;
  }

  getExternalContactsCache(): IPortfolioCompanyContact[] {
    return this.externalContactsCache;
  }
  updateContact(data: any) {
    return this.apiService.put('PortfolioCompanyContact', data);
  } 
  deleteContact(data: any){
     return this.apiService.delete('PortfolioCompanyContact/', { body: { ...data }}); 
  }
  addContact(data: any) {
    return this.apiService.post('PortfolioCompanyContact/add', data);
  } 
 
}
